import { useCallback, useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { assignTargetIdToQrCode, generateQrCodeUrls, getAllQrCodeUrls, unassignQrCode as unassignQrCodeApi } from "../../../services/directPayQrCodes";

export interface QrCodeUrl {
	qrCodeUrl: string;
	target: string | null;
	isLocation: boolean | null;
}

type ToastLevel = "success" | "error" | "warn" | "info";

const showToast = (
	toastRef: React.RefObject<Toast>,
	severity: ToastLevel,
	summary: string,
	detail: string,
	life = 4000
) => {
	toastRef.current?.show({ severity, summary, detail, life });
};

export function useQrCodes(toastRef: React.RefObject<Toast>) {
	const [qrcodes, setQrcodes] = useState<QrCodeUrl[]>([]);
	const [initialLoading, setInitialLoading] = useState<boolean>(true);
	const [refreshing, setRefreshing] = useState<boolean>(false);
	// Tracks the first load in a ref rather than reading initialLoading, so this
	// callback keeps a stable identity. Depending on the state would rebuild
	// fetchQrCodes the moment the first load finishes, and the mount effect
	// below would fire a second request.
	const initialLoadDone = useRef(false);

	const fetchQrCodes = useCallback(async () => {
		const firstLoad = !initialLoadDone.current;
		if (!firstLoad) setRefreshing(true);
		try {
			const data = await getAllQrCodeUrls();
			setQrcodes(data);
		} catch (e: any) {
			const msg = e?.response?.data?.error ?? "Failed to load QR codes.";
			showToast(toastRef, "error", "Error", msg);
		} finally {
			if (firstLoad) {
				initialLoadDone.current = true;
				setInitialLoading(false);
			} else setRefreshing(false);
		}
	}, [toastRef]);

	const assignTargetId = useCallback(
		async (url: string, targetId: string, isLocation: boolean) => {
			try {
				const message = await assignTargetIdToQrCode(url, targetId, isLocation);
				showToast(toastRef, "success", "Success", message);
				await fetchQrCodes();
			} catch (e: any) {
				const msg = e?.response?.data?.error ?? "Failed to assign EVSE.";
				showToast(toastRef, "error", "Error", msg);
				throw e;
			}
		},
		[fetchQrCodes, toastRef]
	);

	const unassignQrCode = useCallback(
		async (url: string) => {
			try {
				const message = await unassignQrCodeApi(url);
				showToast(toastRef, "success", "Success", message);
				await fetchQrCodes();
			} catch (e: any) {
				const msg = e?.response?.data?.error ?? "Failed to unassign QR code.";
				showToast(toastRef, "error", "Error", msg);
				throw e;
			}
		},
		[fetchQrCodes, toastRef]
	);

	const generateMore = useCallback(
		async (quantity: number) => {
			if (quantity <= 0) {
				showToast(toastRef, "warn", "Warning", "Quantity must be greater than 0");
				return;
			}
			try {
				await generateQrCodeUrls(quantity);
				showToast(
					toastRef,
					"success",
					"Success",
					`${quantity} QR Codes generated successfully.`
				);
				await fetchQrCodes();
			} catch (e: any) {
				const msg = e?.response?.data?.error ?? "Failed to generate QR Codes.";
				showToast(toastRef, "error", "Error", msg);
				throw e;
			}
		},
		[fetchQrCodes, toastRef]
	);

	useEffect(() => {
		fetchQrCodes();
	}, [fetchQrCodes]);

	return { qrcodes, initialLoading, refreshing, fetchQrCodes, assignTargetId, unassignQrCode, generateMore };
}